import { Suspense, lazy, useEffect } from "react";
import ScrollToTop from "./helpers/scroll-top";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import ResetPassword from "./pages/other/ResetPassword";
import { useAuth } from "./context/Auth";
import ForgotPassword from "./pages/other/ForgotPassword";
import { store } from "./store/store";
import { setProducts } from "./store/slices/product-slice";
import { API_BASE_URL } from "./utils";
import Returns from "./wrappers/footer/Returns";
import Privacy from "./wrappers/footer/Privacy";
import Kvkk from "./wrappers/footer/Kvkk";
import Kvk from "./wrappers/footer/Kvk";
import Bilgi from "./wrappers/footer/Bilgi";
import Mesafeli from "./wrappers/footer/Mesafeli";
import Cookiesw from "./wrappers/footer/Cookies";
import Teslimat from "./wrappers/footer/Teslimatta";
import Teslimatta from "./wrappers/footer/Teslimatta";

// home pages
const HomeFashion = lazy(() => import("./pages/home/HomeFashion"));
const HomeFashionTwo = lazy(() => import("./pages/home/HomeFashionTwo"));
const HomeFashionThree = lazy(() => import("./pages/home/HomeFashionThree"));
const HomeFashionFour = lazy(() => import("./pages/home/HomeFashionFour"));
const HomeFashionFive = lazy(() => import("./pages/home/HomeFashionFive"));
const HomeFashionSix = lazy(() => import("./pages/home/HomeFashionSix"));
const HomeFashionSeven = lazy(() => import("./pages/home/HomeFashionSeven"));
const HomeFashionEight = lazy(() => import("./pages/home/HomeFashionEight"));
const HomeKidsFashion = lazy(() => import("./pages/home/HomeKidsFashion"));
const HomeCosmetics = lazy(() => import("./pages/home/HomeCosmetics"));
const HomeFurniture = lazy(() => import("./pages/home/HomeFurniture"));
const HomeFurnitureTwo = lazy(() => import("./pages/home/HomeFurnitureTwo"));
const HomeFurnitureThree = lazy(() =>
  import("./pages/home/HomeFurnitureThree")
);
const HomeFurnitureFour = lazy(() => import("./pages/home/HomeFurnitureFour"));
const HomeFurnitureFive = lazy(() => import("./pages/home/HomeFurnitureFive"));
const HomeFurnitureSix = lazy(() => import("./pages/home/HomeFurnitureSix"));
const HomeFurnitureSeven = lazy(() =>
  import("./pages/home/HomeFurnitureSeven")
);
const HomeElectronics = lazy(() => import("./pages/home/HomeElectronics"));
const HomeElectronicsTwo = lazy(() =>
  import("./pages/home/HomeElectronicsTwo")
);
const HomeElectronicsThree = lazy(() =>
  import("./pages/home/HomeElectronicsThree")
);
const HomeBookStore = lazy(() => import("./pages/home/HomeBookStore"));
const HomeBookStoreTwo = lazy(() => import("./pages/home/HomeBookStoreTwo"));
const HomePlants = lazy(() => import("./pages/home/HomePlants"));
const HomeFlowerShop = lazy(() => import("./pages/home/HomeFlowerShop"));
const HomeFlowerShopTwo = lazy(() => import("./pages/home/HomeFlowerShopTwo"));
const HomeOrganicFood = lazy(() => import("./pages/home/HomeOrganicFood"));
const HomeOrganicFoodTwo = lazy(() =>
  import("./pages/home/HomeOrganicFoodTwo")
);
const HomeOnepageScroll = lazy(() => import("./pages/home/HomeOnepageScroll"));
const HomeGridBanner = lazy(() => import("./pages/home/HomeGridBanner"));
const HomeAutoParts = lazy(() => import("./pages/home/HomeAutoParts"));
const HomeCakeShop = lazy(() => import("./pages/home/HomeCakeShop"));
const HomeHandmade = lazy(() => import("./pages/home/HomeHandmade"));
const HomePetFood = lazy(() => import("./pages/home/HomePetFood"));
const HomeMedicalEquipment = lazy(() =>
  import("./pages/home/HomeMedicalEquipment")
);
const HomeChristmas = lazy(() => import("./pages/home/HomeChristmas"));
const HomeBlackFriday = lazy(() => import("./pages/home/HomeBlackFriday"));
const HomeBlackFridayTwo = lazy(() =>
  import("./pages/home/HomeBlackFridayTwo")
);
const HomeValentinesDay = lazy(() => import("./pages/home/HomeValentinesDay"));

// shop pages
const ShopGridStandard = lazy(() => import("./pages/shop/ShopGridStandard"));
const ShopGridFilter = lazy(() => import("./pages/shop/ShopGridFilter"));
const ShopGridTwoColumn = lazy(() => import("./pages/shop/ShopGridTwoColumn"));
const ShopGridNoSidebar = lazy(() => import("./pages/shop/ShopGridNoSidebar"));
const ShopGridFullWidth = lazy(() => import("./pages/shop/ShopGridFullWidth"));
const ShopGridRightSidebar = lazy(() =>
  import("./pages/shop/ShopGridRightSidebar")
);
const ShopListStandard = lazy(() => import("./pages/shop/ShopListStandard"));
const ShopListFullWidth = lazy(() => import("./pages/shop/ShopListFullWidth"));
const ShopListTwoColumn = lazy(() => import("./pages/shop/ShopListTwoColumn"));

// product pages
const Product = lazy(() => import("./pages/shop-product/Product"));
const ProductTabLeft = lazy(() =>
  import("./pages/shop-product/ProductTabLeft")
);
const ProductTabRight = lazy(() =>
  import("./pages/shop-product/ProductTabRight")
);
const ProductSticky = lazy(() => import("./pages/shop-product/ProductSticky"));
const ProductSlider = lazy(() => import("./pages/shop-product/ProductSlider"));
const ProductFixedImage = lazy(() =>
  import("./pages/shop-product/ProductFixedImage")
);

// blog pages
const BlogStandard = lazy(() => import("./pages/blog/BlogStandard"));
const BlogNoSidebar = lazy(() => import("./pages/blog/BlogNoSidebar"));
const BlogRightSidebar = lazy(() => import("./pages/blog/BlogRightSidebar"));
const BlogDetailsStandard = lazy(() =>
  import("./pages/blog/BlogDetailsStandard")
);

// other pages
const About = lazy(() => import("./pages/other/About"));
const Contact = lazy(() => import("./pages/other/Contact"));
const MyAccount = lazy(() => import("./pages/other/MyAccount"));
const LoginRegister = lazy(() => import("./pages/other/LoginRegister"));

const Cart = lazy(() => import("./pages/other/Cart"));
const Wishlist = lazy(() => import("./pages/other/Wishlist"));
const Compare = lazy(() => import("./pages/other/Compare"));
const Checkout = lazy(() => import("./pages/other/Checkout"));

const NotFound = lazy(() => import("./pages/other/NotFound"));

const App = () => {
  const { authenticated } = useAuth();

  useEffect(() => {
    const getProduct = async () => {
      try {
        const res = await fetch(API_BASE_URL + "/product");
        const data = await res.json();
        const products = data?.map((product) => ({
          id: product?.id,
          sku: product?.id,
          name: product?.title || "",
          nameAr: product?.titleAr || "",
          nameTr: product?.titleTr || "",
          shortDescription: product?.brief || "",
          shortDescriptionAr: product?.briefAr || "",
          shortDescriptionTr: product?.briefTr || "",
          fullDescription: product?.description || "",
          fullDescriptionAr: product?.descriptionAr || "",
          fullDescriptionTr: product?.descriptionTr || "",
          category: [product?.category?.title],
          categoryAr: [product?.category?.titleAr],
          categoryTr: [product?.category?.titleTr],
          tag: product?.tags,
          stock: product?.quantity || 0,
          price: product?.price || 0,
          image:
            product?.images
              ?.filter((image) => image?.order !== null)
              ?.sort((a, b) => a?.order - b?.order)
              .map((image) => image?.url) || [], // Sort images by order from lowest to highest
          new: product?.markedAsNew || false,
          discount:
            product?.price >= product?.oldPrice
              ? 0
              : (100 - (product?.price / product?.oldPrice) * 100).toFixed(0) ||
                0,
          oldPrice: product?.oldPrice || 0,
          rating:
            product?.reviews?.length > 0
              ? product?.reviews.reduce(
                  (sum, review) => sum + (review?.rate || 0),
                  0
                ) / product?.reviews.length
              : 0,
          weight: product?.weight || 0,
          type: product?.type || "",
          skinType: product?.skinType || "",
          material: product?.materials || "",
          reviews: product?.reviews || [],
        }));

        // Dispatch products array directly
        store.dispatch(setProducts(products));
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    };

    getProduct();
  }, []);

  const products = store.getState().product?.products;
  console.log(products);

  return (
    <Router>
      <ScrollToTop>
        <Suspense
          fallback={
            <div className="flone-preloader-wrapper">
              <div className="flone-preloader">
                <span></span>
                <span></span>
              </div>
            </div>
          }
        >
          <Routes>
            <Route
              path={process.env.PUBLIC_URL + "/"}
              element={<HomeCosmetics />}
            />

            {/* Homepages */}

            <Route
              path={process.env.PUBLIC_URL + "/home-cosmetics"}
              element={<HomeCosmetics />}
            />

            {/* Shop pages */}
            <Route
              path={process.env.PUBLIC_URL + "/shop-grid-standard"}
              element={<ShopGridStandard />}
            />

            {/* Shop product pages */}
            <Route
              path={process.env.PUBLIC_URL + "/product/:id"}
              element={<Product />}
            />

            {/* Blog pages 
              <Route
                path={process.env.PUBLIC_URL + "/blog-standard"}
                element={<BlogStandard/>}
              />
              <Route
                path={process.env.PUBLIC_URL + "/blog-no-sidebar"}
                element={<BlogNoSidebar/>}
              />
              <Route
                path={process.env.PUBLIC_URL + "/blog-right-sidebar"}
                element={<BlogRightSidebar/>}
              />
              <Route
                path={process.env.PUBLIC_URL + "/blog-details-standard"}
                element={<BlogDetailsStandard/>
              /> */}

            {/* Other pages */}
            <Route
              path={process.env.PUBLIC_URL + "/about"}
              element={<About />}
            />
            <Route
              path={process.env.PUBLIC_URL + "/satissozlesmesi"}
              element={<Mesafeli />}
            />
            <Route
              path={process.env.PUBLIC_URL + "/teslimat"}
              element={<Teslimatta />}
            />
             <Route
              path={process.env.PUBLIC_URL + "/cookies"}
              element={<Cookiesw />}
            />
             <Route
              path={process.env.PUBLIC_URL + "/onbilgilendirme"}
              element={<Bilgi />}
            />
             <Route
              path={process.env.PUBLIC_URL + "/returns"}
              element={<Returns />}
            />
            <Route
              path={process.env.PUBLIC_URL + "/kvkk"}
              element={<Kvk />}
            />
            <Route
              path={process.env.PUBLIC_URL + "/kvkk-form"}
              element={<Kvkk />}
            />
             <Route
              path={process.env.PUBLIC_URL + "/privacy-policy"}
              element={<Privacy />}
            />
            <Route
              path={process.env.PUBLIC_URL + "/contact"}
              element={<Contact />}
            />
            <Route
              path={process.env.PUBLIC_URL + "/my-account"}
              element={
                authenticated ? (
                  <MyAccount />
                ) : (
                  <Navigate to={process.env.PUBLIC_URL + "/login-register"} />
                )
              }
            />
            <Route
              path={process.env.PUBLIC_URL + "/login-register"}
              element={
                authenticated ? (
                  <Navigate to={process.env.PUBLIC_URL + "/my-account"} />
                ) : (
                  <LoginRegister />
                )
              }
            />
            <Route
              path={process.env.PUBLIC_URL + "/reset-password"}
              element={
                authenticated ? (
                  <Navigate to={process.env.PUBLIC_URL + "/my-account"} />
                ) : (
                  <ResetPassword />
                )
              }
            />
            <Route
              path={process.env.PUBLIC_URL + "/forgot-password"}
              element={
                authenticated ? (
                  <Navigate to={process.env.PUBLIC_URL + "/my-account"} />
                ) : (
                  <ForgotPassword />
                )
              }
            />

            <Route path={process.env.PUBLIC_URL + "/cart"} element={<Cart />} />
            <Route
              path={process.env.PUBLIC_URL + "/wishlist"}
              element={<Wishlist />}
            />
            <Route
              path={process.env.PUBLIC_URL + "/compare"}
              element={<Compare />}
            />
            <Route
              path={process.env.PUBLIC_URL + "/checkout"}
              element={<Checkout />}
            />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </ScrollToTop>
    </Router>
  );
};

export default App;
